import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import queryString from "query-string";
import { redeemPortalOtt } from "actions/handoff";

// Resolves the credential for a page that was opened by another first-party app.
//
// Two shapes arrive here and both must keep working:
//   * `?ott=`   — a one-time handoff token. Redeemed once for a session JWT.
//                 This is what the mobile app now sends (S3DEV2-7560).
//   * `?token=` — a token directly in the URL. Still used by in-flight email and
//                 SMS links, so it keeps working untouched.
//
// The redeemed JWT is kept in sessionStorage for the tab's lifetime, which is
// what makes a reload survivable: the OTT is consumed by the first redemption,
// so a second attempt with the same handle would be rejected.

const SESSION_KEY = "portal_handoff_jwt";

const readStored = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) || null;
  } catch {
    // sessionStorage throws in private modes / sandboxed frames.
    return null;
  }
};

const writeStored = token => {
  try {
    if (token) sessionStorage.setItem(SESSION_KEY, token);
  } catch {
    /* best effort — the in-memory value still serves this page load */
  }
};

// Drop `ott` from the address bar without navigating, preserving every other
// param, so it is not re-submitted on reload or captured in history/referer.
const stripOttFromUrl = () => {
  const parsed = queryString.parse(window.location.search);
  if (!("ott" in parsed)) return;
  delete parsed.ott;
  const rest = queryString.stringify(parsed);
  window.history.replaceState(
    null,
    "",
    window.location.pathname +
      (rest ? `?${rest}` : "") +
      window.location.hash
  );
};

const useHandoffToken = () => {
  const dispatch = useDispatch();

  // Capture both params on first render and work from the captured values, never
  // from the live URL — we are about to rewrite it.
  const paramsRef = useRef(queryString.parse(window.location.search));
  const ott = paramsRef.current.ott;
  const urlToken = paramsRef.current.token;

  // A JWT already in sessionStorage means this tab redeemed on an earlier render
  // and the handle in the URL is spent. Reuse it rather than burning a second
  // redemption that would fail and strand the patient.
  const storedRef = useRef(readStored());

  const [token, setToken] = useState(
    () => storedRef.current || urlToken || null
  );
  const [isRedeeming, setIsRedeeming] = useState(
    !!ott && !storedRef.current && !urlToken
  );
  const [error, setError] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ott || startedRef.current) return;
    startedRef.current = true;

    if (storedRef.current || urlToken) {
      // Nothing to redeem; just clean up the stale handle.
      stripOttFromUrl();
      return;
    }

    // Strip before the request goes out, not after it resolves: the value is
    // already captured, and a reload mid-flight then reloads a clean URL instead
    // of re-submitting a handle that is about to be consumed.
    stripOttFromUrl();

    let cancelled = false;

    (async () => {
      try {
        const redeemed = await dispatch(redeemPortalOtt(ott));
        if (cancelled) return;
        if (!redeemed) throw new Error("Handoff redemption returned no token");
        writeStored(redeemed);
        setToken(redeemed);
      } catch (e) {
        if (cancelled) return;
        console.error(e.message);
        setError(true);
      } finally {
        if (!cancelled) setIsRedeeming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ott, urlToken, dispatch]);

  return { token, isRedeeming, error };
};

export default useHandoffToken;
